<template>
  <div class="table-section">
    <div class="table-header">
      <h2>{{ title }}</h2>
      <div class="table-actions">
        <span class="record-count">총 {{ recordCount }}건</span>
        <slot name="actions"></slot>
      </div>
    </div>

    <div class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th 
              v-for="column in columns" 
              :key="column.key"
              :style="getColumnStyle(column)"
            >
              {{ column.title }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="columns.length" class="loading-cell">
              <div class="loading-spinner">{{ loadingText }}</div>
            </td>
          </tr>
          <tr v-else-if="data.length === 0">
            <td :colspan="columns.length" class="empty-cell">{{ emptyText }}</td>
          </tr>
          <tr v-else v-for="(item, index) in data" :key="getRowKey(item, index)">
            <td 
              v-for="column in columns" 
              :key="column.key" 
              :class="column.cellClass"
              :style="getColumnStyle(column)"
            >
              <slot 
                :name="`cell-${column.key}`" 
                :item="item" 
                :value="getNestedValue(item, column.key)"
                :index="index"
              >
                {{ getNestedValue(item, column.key) }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  columns: {
    type: Array,
    required: true,
    validator: (columns) => {
      return columns.every(col => col.key && col.title)
    }
  },
  data: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  loadingText: {
    type: String,
    default: '로딩 중...'
  },
  emptyText: {
    type: String,
    default: '데이터가 없습니다.'
  },
  rowKey: {
    type: [String, Function],
    default: null
  }
})

const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

const getRowKey = (item, index) => {
  if (typeof props.rowKey === 'function') {
    return props.rowKey(item, index)
  }
  if (typeof props.rowKey === 'string') {
    return getNestedValue(item, props.rowKey)
  }
  return index
}

const recordCount = computed(() => props.data.length)

const getColumnStyle = (column) => {
  const style = {}
  if (column.width) {
    style.width = column.width
    style.maxWidth = column.width
  }
  if (column.minWidth) {
    style.minWidth = column.minWidth
  }
  return style
}
</script>

<style scoped>
.table-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.table-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.table-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.record-count {
  font-size: 14px;
  color: #666;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #f8f9fa;
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.data-table td {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.loading-cell,
.empty-cell {
  text-align: center;
  padding: 40px 12px;
  color: #666;
  font-style: italic;
}

.loading-spinner {
  display: inline-block;
}

@media (max-width: 768px) {
  .table-container {
    font-size: 14px;
  }
  
  .data-table th,
  .data-table td {
    padding: 8px;
  }
}
</style>